import React from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "../../utils/cn";

const Checkbox = React.forwardRef(({
    className,
    id,
    checked = false,
    indeterminate = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    size = "default",
    onChange,
    onCheckedChange,
    ...props
}, ref) => {
    // Generate stable unique ID if not provided
    const [fallbackId] = React.useState(() => `checkbox-${Math.random().toString(36).substr(2, 9)}`);
    const checkboxId = id || fallbackId;

    // Size variants
    const sizeClasses = {
        sm: "h-4 w-4",
        default: "h-4 w-4",
        lg: "h-5 w-5"
    };

    const isCurrentChecked = Boolean(checked);

    const triggerChange = (nextChecked, originalEvent) => {
        if (disabled) return;

        // 1. If onCheckedChange is provided, pass the boolean value
        if (typeof onCheckedChange === 'function') {
            onCheckedChange(nextChecked);
        }

        // 2. If onChange is provided, pass synthetic event with target.checked
        if (typeof onChange === 'function') {
            const syntheticEvent = {
                target: {
                    type: 'checkbox',
                    id: checkboxId,
                    checked: nextChecked,
                    name: props.name || '',
                    value: props.value || 'on',
                },
                currentTarget: {
                    type: 'checkbox',
                    id: checkboxId,
                    checked: nextChecked,
                    name: props.name || '',
                    value: props.value || 'on',
                },
                preventDefault: () => originalEvent?.preventDefault?.(),
                stopPropagation: () => originalEvent?.stopPropagation?.(),
            };
            onChange(syntheticEvent, nextChecked);
        }
    };

    return (
        <div 
            className={cn("flex items-start space-x-2.5 select-none cursor-pointer", disabled && "cursor-not-allowed opacity-50", className)}
            onClick={(e) => {
                if (!disabled) {
                    e.preventDefault();
                    triggerChange(!isCurrentChecked, e);
                }
            }}
        >
            <div className="relative flex items-center mt-0.5">
                <input
                    type="checkbox"
                    ref={ref}
                    id={checkboxId}
                    checked={isCurrentChecked}
                    disabled={disabled}
                    required={required}
                    onChange={(e) => {
                        e.stopPropagation();
                        triggerChange(e.target.checked, e);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="sr-only"
                    {...props}
                />

                <div
                    className={cn(
                        "peer shrink-0 rounded border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 cursor-pointer transition-colors flex items-center justify-center",
                        sizeClasses[size],
                        isCurrentChecked && "bg-primary text-primary-foreground border-primary",
                        indeterminate && "bg-primary text-primary-foreground border-primary",
                        error && "border-destructive",
                        disabled && "cursor-not-allowed opacity-50"
                    )}
                >
                    {isCurrentChecked && !indeterminate && (
                        <Check className="h-3 w-3 text-current stroke-[3]" />
                    )}
                    {indeterminate && (
                        <Minus className="h-3 w-3 text-current stroke-[3]" />
                    )}
                </div>
            </div>

            {(label || description || error) && (
                <div className="flex-1 space-y-0.5">
                    {label && (
                        <label
                            htmlFor={checkboxId}
                            className={cn(
                                "text-sm font-medium leading-none cursor-pointer block select-none",
                                disabled && "cursor-not-allowed opacity-70",
                                error ? "text-destructive" : "text-foreground"
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                        >
                            {label}
                            {required && <span className="text-destructive ml-1">*</span>}
                        </label>
                    )}

                    {description && !error && (
                        <p className="text-xs text-muted-foreground leading-normal select-none">
                            {description}
                        </p>
                    )}

                    {error && (
                        <p className="text-xs text-destructive leading-normal select-none">
                            {error}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
});

Checkbox.displayName = "Checkbox";

export { Checkbox };
export default Checkbox;
